import React from 'react';
import { createHelpers, createResource, ResourceField } from '@scalingworks/refine-react-admin';
import { getSdk, SellerResource, StampTemplate } from '~/api';
import {
  Loading,
  SomethingWentWrong,
  FormBuilder,
  ActionButton,
} from '~/components';
import {
  useCreate,
  useNavigation,
  useOne,
  useUpdate,
  useTranslate,
} from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { useForm } from '@refinedev/react-hook-form';
import { resourceNames } from '../../resource-names';
import { ResourceContext } from '~/resources/type';
import { FaStamp } from 'react-icons/fa';
import { renderStatusTag } from '../helpers';
import { StampTemplateFormWizard } from './components/StampTemplateFormWizard';
import { StampTemplateShowPage } from './show';

const { defineFields } = createHelpers<StampTemplate>({
  resourceName: resourceNames.stampTemplate,
});

export const fields: ResourceField<StampTemplate>[] = [
  'id',
  'name',
  'description',
  'totalStampsRequired',
  'isActive',
  'validityDays',
  'cardImageUrl',
  'displayOrder',
  'autoActivate',
  'allowDuplicates',
  'maxCardsPerCustomer',
  'autoCreateOnOverflow',
  'metadata',
  'iconUrl',
  'iconFilledUrl',
  'cardImageUrl',
];

export const stampTemplateResource = (context: ResourceContext) => {
  return createResource({
    name: resourceNames.stampTemplate,
    label: 'Stamp Templates',
    fields: defineFields(fields),
    icon: <FaStamp />,
    allowCreate: true,
    allowEdit: true,
    allowDelete: true,
    allowSearch: true,
    searchConfig: {
      placeholder: ({ t }) =>
        t('stampTemplates.placeholder.search', {
          fallback: 'Search loyalty card templates...',
          ns: 'common',
        }),
    },
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
      autoActivate: true,
      allowDuplicates: false,
      totalStampsRequired: 10,
      validityDays: 90,
      maxCardsPerCustomer: 1,
      autoCreateOnOverflow: false,
      displayOrder: null,
      cardImageUrl: '',
      iconUrl: '',
      iconFilledUrl: '',
      iconWithRewardUrl: '',
      iconWithRewardFilledUrl: '',
      eligibilityCheckers: [] as any,
      stampingRules: [] as any,
      milestones: [] as any,
      metadata: {},
      termsAndConditions: '',
    } as any,
    columns: ({ LinkToDetails, t }) => [
      {
        id: 'name',
        header: t('stampTemplates.column.name', { fallback: 'Template Name', ns: 'common' }),
        cell: (data) => {
          const { id, name } = data.row.original as StampTemplate;
          return (
            <LinkToDetails resourceId={id}>
              <span>{name}</span>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'description',
        header: t('stampTemplates.column.description', {
          fallback: 'Template Description',
          ns: 'common',
        }),
        cell: (data) => {
          const { description } = data.row.original as StampTemplate;
          return <span>{description || '-'}</span>;
        },
      },
      {
        id: 'totalStampsRequired',
        header: t('stampTemplates.column.totalStampsRequired', {
          fallback: 'Required Stamps',
          ns: 'common',
        }),
        cell: (data) => {
          const { totalStampsRequired } = data.row.original as StampTemplate;
          return <span>{totalStampsRequired}</span>;
        },
      },
      {
        id: 'isActive',
        header: t('stampTemplates.column.status', { fallback: 'Template Status', ns: 'common' }),
        cell: (data) => {
          const { isActive } = data.row.original as StampTemplate;
          return renderStatusTag(isActive, resourceNames.stampTemplate);
        },
      },
      {
        id: 'validityDays',
        header: t('stampTemplates.column.validityDays', { fallback: 'Valid For', ns: 'common' }),
        cell: (data) => {
          const { validityDays } = data.row.original as StampTemplate;
          return (
            <span>
              {validityDays
                ? `${validityDays} days`
                : t('common.noExpiry', { fallback: 'Never expires', ns: 'common' })}
            </span>
          );
        },
      },
      {
        id: 'autoActivate',
        header: t('stampTemplates.column.autoActivate', {
          fallback: 'Auto-Activation',
          ns: 'common',
        }),
        cell: (data) => {
          const { autoActivate } = data.row.original as StampTemplate;
          return (
            <span>
              {t(autoActivate ? 'common.yes' : 'common.no', {
                fallback: autoActivate ? 'Yes' : 'No',
                ns: 'common',
              })}
            </span>
          );
        },
      },
    ],
    dataProvider: {
      getList: ({ client, pagination, sort, filters }) => {
        const take = pagination?.pageSize || 20;
        const skip = ((pagination?.current || 1) - 1) * take;

        return getSdk(client)
          ?.GetStampTemplates({
            options: {
              take,
              skip,
            },
          })
          ?.then((res) => ({
            data: res?.stampTemplates?.items || [],
            total: res?.stampTemplates?.totalItems || 0,
          }));
      },
      getOne: ({ client, id }) => {
        return (
          getSdk(client)
            ?.GetStampTemplate({
              id: id as string,
            })
            ?.then((res) => {
              const template = res?.stampTemplate;
              if (!template) return { data: {} };

              // Parse JSON fields if they come as strings
              let eligibilityCheckers = template.eligibilityCheckers;
              let stampingRules = template.stampingRules;
              let milestones = template.milestones;

              // If fields come as JSON strings, parse them
              try {
                if (typeof eligibilityCheckers === 'string') {
                  eligibilityCheckers = JSON.parse(eligibilityCheckers);
                }
                if (typeof stampingRules === 'string') {
                  stampingRules = JSON.parse(stampingRules);
                }
                if (typeof milestones === 'string') {
                  milestones = JSON.parse(milestones);
                }
              } catch (e) {
                console.warn('Error parsing JSON fields:', e);
              }

              // Transform backend format to frontend format
              const transformedData = {
                ...template,
                // Transform eligibilityCheckers from backend format to frontend format
                eligibilityCheckers: (eligibilityCheckers || []).map((checker: any) => ({
                  code: checker.type || checker.code,
                  params: checker.config || checker.params || {},
                  description: checker.description,
                })),
                // Transform stampingRules from backend format to frontend format
                stampingRules: (stampingRules || []).map((rule: any) => ({
                  checkers: (rule.checkers || []).map((checker: any) => ({
                    code: checker.type || checker.code,
                    params: checker.config || checker.params || {},
                    description: checker.description,
                  })),
                  action: rule.action
                    ? {
                        code: rule.action.type || rule.action.code,
                        params: rule.action.config || rule.action.params || {},
                        description: rule.action.description,
                      }
                    : { code: '', params: {} },
                  priority: rule.priority || 1,
                })),
                // Transform milestones from backend format to frontend format
                milestones: (milestones || []).map((milestone: any) => ({
                  milestoneStamps: milestone.milestoneStamps || milestone.stampsRequired || 0,
                  name: milestone.name || '',
                  description: milestone.description || '',
                  rewardType: milestone.rewardType || 'NONE',
                  rewardValue: milestone.rewardValue || '0',
                  autoClaimable: milestone.autoClaimable || false,
                  iconUrl: milestone.iconUrl || '',
                })),
              };

              console.log('getOne transformedData:', {
                eligibilityCheckers: transformedData.eligibilityCheckers,
                milestones: transformedData.milestones,
              });
              return { data: transformedData };
            }) || Promise.resolve({ data: {} })
        );
      },
      create: async ({ client, variables }) => {
        // Transform eligibilityCheckers from frontend format to backend format
        const eligibilityCheckers = (variables.eligibilityCheckers || []).map((checker: any) => ({
          code: checker.code || checker.type,
          params: checker.params || checker.config || {},
        }));

        // Transform stampingRules from frontend format to backend format
        const stampingRules = (variables.stampingRules || []).map((rule: any) => ({
          checkers: (rule.checkers || []).map((checker: any) => ({
            code: checker.code || checker.type,
            params: checker.params || checker.config || {},
          })),
          action: rule.action
            ? {
                code: rule.action.code || rule.action.type,
                params: rule.action.params || rule.action.config || {},
              }
            : {
                code: 'add-stamps',
                params: { count: 1 },
              },
          priority: rule.priority || 1,
        }));

        // Prepare the input - include all fields
        const input: any = {
          name: variables.name ?? '',
          description: variables.description ?? '',
          totalStampsRequired: variables.totalStampsRequired
            ? Number(variables.totalStampsRequired)
            : 0,
          isActive: variables.isActive ?? false,
          validityDays: variables.validityDays ? Number(variables.validityDays) : null,
          autoActivate: variables.autoActivate ?? false,
          allowDuplicates: variables.allowDuplicates ?? false,
          cardImageUrl: variables.cardImageUrl ?? '',
          // Add new fields that backend supports
          autoCreateOnOverflow: variables.autoCreateOnOverflow ?? false,
          maxCardsPerCustomer: variables.maxCardsPerCustomer
            ? Number(variables.maxCardsPerCustomer)
            : 1,
          displayOrder: variables.displayOrder ? Number(variables.displayOrder) : null,
          eligibilityCheckers: eligibilityCheckers,
          stampingRules: stampingRules,
          milestones: (variables.milestones || []).map((milestone: any) => ({
            milestoneStamps: milestone.stampsRequired || milestone.milestoneStamps || 0,
            name:
              milestone.name ||
              `Milestone at ${milestone.stampsRequired || milestone.milestoneStamps || 0} stamps`,
            description: milestone.description || '',
            rewardType: milestone.rewardType || 'NONE',
            rewardValue: milestone.rewardValue || 0,
            autoClaimable: milestone.autoClaimable || false,
          })),
          metadata: variables.metadata || {},
          termsAndConditions: variables.termsAndConditions || '',
        };

        return (
          getSdk(client)
            ?.CreateStampTemplate({ input })
            ?.then((res) => ({
              data: res?.createStampTemplate || {},
            })) || Promise.resolve({ data: {} })
        );
      },
      update: async ({ client, id, variables }) => {
        // Transform eligibilityCheckers from frontend format to backend format
        const eligibilityCheckers = variables.eligibilityCheckers
          ? (variables.eligibilityCheckers || []).map((checker: any) => ({
              code: checker.code || checker.type,
              params: checker.params || checker.config || {},
            }))
          : undefined;

        // Transform stampingRules from frontend format to backend format
        const stampingRules = variables.stampingRules
          ? (variables.stampingRules || []).map((rule: any) => ({
              checkers: (rule.checkers || []).map((checker: any) => ({
                code: checker.code || checker.type,
                params: checker.params || checker.config || {},
              })),
              action: {
                code: rule.action.code || rule.action.type,
                params: rule.action.params || rule.action.config || {},
              },
              priority: rule.priority || 1,
            }))
          : undefined;

        // Prepare the input - only include fields that exist in the GraphQL schema
        const input: any = {
          id: id as string,
          name: variables.name,
          description: variables.description,
          totalStampsRequired: variables.totalStampsRequired
            ? Number(variables.totalStampsRequired)
            : undefined,
          isActive: variables.isActive,
          validityDays: variables.validityDays ? Number(variables.validityDays) : undefined,
          autoActivate: variables.autoActivate,
          allowDuplicates: variables.allowDuplicates,
          cardImageUrl: variables.cardImageUrl,
          iconUrl: variables.iconUrl,
          iconFilledUrl: variables.iconFilledUrl,
          termsAndConditions: variables.termsAndConditions,
        };

        // Add new fields that backend supports
        if (variables.autoCreateOnOverflow !== undefined) {
          input.autoCreateOnOverflow = variables.autoCreateOnOverflow;
        }
        if (variables.maxCardsPerCustomer !== undefined) {
          input.maxCardsPerCustomer = Number(variables.maxCardsPerCustomer);
        }
        if (variables.displayOrder !== undefined) {
          input.displayOrder = Number(variables.displayOrder);
        }
        // Send complex fields as objects, not JSON strings
        if (eligibilityCheckers !== undefined) {
          input.eligibilityCheckers = eligibilityCheckers;
        }
        if (stampingRules !== undefined) {
          input.stampingRules = stampingRules;
        }
        if (variables.milestones !== undefined) {
          input.milestones = (variables.milestones || []).map((milestone: any) => ({
            milestoneStamps: milestone.stampsRequired || milestone.milestoneStamps || 0,
            name:
              milestone.name ||
              `Milestone at ${milestone.stampsRequired || milestone.milestoneStamps || 0} stamps`,
            description: milestone.description || '',
            rewardType: milestone.rewardType || 'NONE',
            rewardValue: String(milestone.rewardValue || '0'),
            autoClaimable: milestone.autoClaimable || false,
            iconUrl: milestone.iconUrl || '',
          }));
        }
        if (variables.metadata !== undefined) {
          input.metadata = variables.metadata;
        }

        input.iconWithRewardUrl = variables.iconWithRewardUrl;
        input.iconWithRewardFilledUrl = variables.iconWithRewardFilledUrl;
        console.log('@ALL INPUT', input);
        return (
          getSdk(client)
            ?.UpdateStampTemplate({ input })
            ?.then((res) => ({
              data: res?.updateStampTemplate || {},
            })) || Promise.resolve({ data: {} })
        );
      },
      deleteOne: async ({ client, id }) => {
        return getSdk(client)
          ?.DeleteStampTemplate({
            id: id as string,
          })
          ?.then(() => ({
            data: { id },
          }));
      },
    },
    create: {
      render: () => {
        const t = useTranslate();
        const navigation = useNavigation();
        const { mutate, isLoading } = useCreate();
        const form = useForm({
          defaultValues: {
            isActive: true,
            autoActivate: true,
            allowDuplicates: false,
            totalStampsRequired: 10,
            validityDays: 90,
            maxCardsPerCustomer: 1,
            eligibilityCheckers: [],
            stampingRules: [],
            milestones: [],
            termsAndConditions: '',
          },
        });

        const onSubmit = async (formData: Partial<StampTemplate>) => {
          try {
            await mutate(
              {
                resource: resourceNames.stampTemplate,
                values: formData,
              },
              {
                onSuccess: () => {
                  navigation?.list(resourceNames.stampTemplate);
                },
                onError: (error) => {
                  console.error('Failed to create stamp template:', error);
                  // Error notification will be handled by Refine's notification provider
                },
              }
            );
          } catch (error) {
            console.error('Unexpected error:', error);
          }
        };

        return (
          <StampTemplateFormWizard
            form={form}
            onSubmit={onSubmit}
            title={t('stampTemplates.create.name', {
              fallback: 'Create Loyalty Card Template',
              ns: 'common',
            })}
            loading={isLoading}
          />
        );
      },
    },
    edit: {
      render: () => {
        const { id } = useParams();
        const t = useTranslate();
        const navigation = useNavigation();
        const { mutate, isLoading: isUpdating } = useUpdate();

        if (!id) return <SomethingWentWrong />;

        const { data, isLoading } = useOne({
          resource: resourceNames.stampTemplate,
          id,
          metaData: {
            fields,
          },
        });
        const stampTemplateData = data?.data as StampTemplate;
        console.log('stampTemplateData', stampTemplateData);
        // Initialize form with empty defaults first
        const form = useForm({
          defaultValues: {
            isActive: true,
            autoActivate: true,
            allowDuplicates: false,
            totalStampsRequired: 10,
            validityDays: 90,
            eligibilityCheckers: [],
            stampingRules: [],
            milestones: [],
            termsAndConditions: '',
          },
        });

        const onSubmit = async (formData: Partial<StampTemplate>) => {
          try {
            await mutate(
              {
                id,
                resource: resourceNames.stampTemplate,
                values: formData,
              },
              {
                onError: (error) => {
                  console.error('Failed to update stamp template:', error);
                  // Error notification will be handled by Refine's notification provider
                },
              }
            );
          } catch (error) {
            console.error('Unexpected error:', error);
          }
        };

        if (isLoading) return <Loading />;

        return (
          <StampTemplateFormWizard
            form={form}
            onSubmit={onSubmit}
            title={t('stampTemplates.edit.name', {
              fallback: 'Edit Loyalty Card Template',
              ns: 'common',
            })}
            isUpdate={true}
            loading={isUpdating}
            defaultValues={stampTemplateData}
          />
        );
      },
    },
    show: {
      component: ({ queryResult, resourceId }) => {
        const { data, isLoading } = queryResult;
        const stampTemplateData = data?.data as StampTemplate;

        return (
          <StampTemplateShowPage
            data={stampTemplateData}
            loading={isLoading}
            resourceId={resourceId as string}
          />
        );
      },
    },
  });
};
